// @ts-ignore
import map from "async/map";
import { DefaultValues } from "react-hook-form";

import {
	CoHostType,
	convertClientRoomProviderToServer,
	convertServerRoomProviderToClient,
	ExtendRoomProvider,
} from "@onair/constants";
import {
	AddressScope,
	CreateEventV2Input,
	DraftEventV2Input,
	EventTypeV2,
	EventV2Response,
	LocationType,
	restRepository,
	RoomMadeBy,
	SessionPriceType,
	SocialCommunityType,
	Ticket,
	TicketScope,
	TicketTier,
	TicketTierType,
} from "@onair/repositories";
import { addWhitespaceToEmptyTypo } from "@onair/utils/text";
import { uploadToS3 } from "@onair/utils/upload-to-s3";

import { WizardSchemaType } from "./";
import { EVENT_SESSION_DEFAULT_VALUE } from "./step-timeline";

type TicketForm = Omit<Ticket, "productId" | "type">;

export const handlePrepareEventSessionFormData = (
	data: EventV2Response["eventSessions"] | null,
): DefaultValues<WizardSchemaType["stepTimeline"]["sessions"]> => {
	if (!data?.length) {
		return [EVENT_SESSION_DEFAULT_VALUE, EVENT_SESSION_DEFAULT_VALUE];
	}

	return data.map((session) => {
		const coHostOnEmail =
			// @ts-ignore
			session?.coHostInfo?.email || session?.coHostInfo?.name;
		return {
			startDate: session?.startDate || null,
			endDate: session?.endDate || null,
			name: session?.name || undefined,
			content: session?.content || undefined,
			banner: session?.banner || undefined,
			// Co-host
			coHostType: coHostOnEmail ? CoHostType.EMAIL : CoHostType.SYSTEM,
			coHost: coHostOnEmail
				? {
						// @ts-ignore
						name: session?.coHostInfo?.name || undefined,
						// @ts-ignore
						email: session?.coHostInfo?.email || undefined,
					}
				: undefined,
			coHostEntity: session?.coHost ? session?.coHost : undefined,
			_coHostSearch: "",
			//
			_id: session?.id || undefined,
		};
	});
};

const _formatTicketTiers = (ticketTiers: TicketForm[]) => {
	return ticketTiers.map((ticketTier) => ({
		id: ticketTier.id || undefined,
		name: ticketTier.name || undefined,
		description: ticketTier.description || undefined,
		farePrice: ticketTier.price || undefined,
		limitAudiences: ticketTier.limitAudiences || undefined,
		limitTicketPerUser: ticketTier.limitTicketPerUser || undefined,
		isFree: ticketTier.price === 0,
		isUnlimitedAudiences: ticketTier.limitAudiences === null,
		isUnlimitedTicketPerUser: ticketTier.limitTicketPerUser === null,
		hasBeenPaid: (ticketTier.paidCount || 0) > 0,
	}));
};

export const handlePrepareEventFormData = (
	event: EventV2Response | null,
): DefaultValues<WizardSchemaType> => {
	let address = "";
	let ticketTiers = [{}];
	let surveyIds: string[] = [];
	let surveyRequired: Record<string, boolean> = {};

	if (event?.productSurveys?.length) {
		event.productSurveys.forEach((survey) => {
			surveyIds.push(survey.surveyId);
			surveyRequired[survey.surveyId] = survey.required || false;
		});
	}

	if (
		event?.locationType === LocationType.ONLINE &&
		(event?.sessionPriceType === SessionPriceType.ALL ||
			event?.type === EventTypeV2.SINGLE)
	) {
		ticketTiers = event.tickets?.length
			? _formatTicketTiers(event.tickets)
			: [{}];
	}

	if (event?.locationType === LocationType.OFFLINE) {
		if (event?.type === EventTypeV2.SINGLE) {
			address = event?.address || "";
			ticketTiers = event.tickets?.length
				? _formatTicketTiers(event.tickets)
				: [{}];
		}

		if (event?.type === EventTypeV2.SESSION) {
			if (event?.addressScope === AddressScope.ALL) {
				address = event?.eventSessions?.[0]?.address || "";
			}

			if (event?.ticketScope === TicketScope.ALL) {
				const combinedTickets: Record<string, TicketForm> = {};

				event?.eventSessions?.forEach((session) => {
					const tickets = session?.tickets || [];
					tickets.forEach((ticket, index) => {
						if (!combinedTickets[index]) {
							combinedTickets[index] = {
								id: ticket.id,
								name: ticket.name,
								description: ticket.description,
								price: ticket.price || 0,
								limitAudiences: ticket.limitAudiences || null,
								limitTicketPerUser: ticket.limitTicketPerUser || null,
								paidCount: ticket.paidCount,
							};
						} else {
							combinedTickets[index].paidCount =
								(combinedTickets[index].paidCount || 0) +
								(ticket.paidCount || 0);
							combinedTickets[index].id =
								combinedTickets[index].id + "-" + ticket.id;
						}
					});
				});

				ticketTiers = _formatTicketTiers(Object.values(combinedTickets));
			}
		}
	}

	return {
		locationType: event?.locationType || undefined,
		type: event?.type || undefined,
		stepInfo: {
			name: event?.name || undefined,
			hashtags: event?.hashtags?.length ? event.hashtags || [] : [],
			categories:
				event?.categoriesV2?.map((category) => ({
					value: category.id,
					label: category.name,
				})) || [],
			content: event?.content || undefined,
			banner: event?.banner || undefined,
			videos: event?.videos?.length
				? event.videos.map((url) => ({ url }))
				: [{ url: "" }],
			photos: event?.photos?.length
				? [...event.photos.map((photo) => ({ file: photo }))]
				: [1, 2, 3].map(() => ({ file: null })),
			_recalculatePhotosAndVideos: 0,
			surveyIds,
			surveyRequired,
		},
		stepTimelineAndPricing: {
			locationType: event?.locationType || undefined,
			startDate: event?.startDate || null,
			endDate: event?.endDate || null,
			roomProvider: event?.roomProvider
				? convertServerRoomProviderToClient(
						event.roomProvider,
						event.roomMadeBy || RoomMadeBy.SYSTEM,
					)
				: undefined,
			roomUrl: event?.roomUrl || undefined,
			roomInfo: event?.roomInfo || undefined,
			socialCommunities:
				event?.product?.socialCommunities?.map((item) => ({
					socialCommunityType: item.type,
					socialCommunityName: item.name,
					socialCommunityLink: item.url,
				})) || [],
			ticketTiers,
			address,
		},
		// Event Session
		stepTimeline: {
			sessions: handlePrepareEventSessionFormData(event?.eventSessions || null),
		},
		stepPricing: {
			ticketScope: event?.ticketScope || TicketScope.ALL,
			addressScope: event?.addressScope || AddressScope.ALL,
			locationType: event?.locationType || undefined,
			priceType: event?.sessionPriceType || SessionPriceType.ALL,
			socialCommunities:
				event?.product?.socialCommunities?.map((item) => ({
					socialCommunityType: item.type,
					socialCommunityName: item.name,
					socialCommunityLink: item.url,
				})) || [],
			ticketTiers,
			address,
			sessions:
				event?.eventSessions?.map((session) => {
					let address = "";
					let ticketTiers = [{}];

					if (
						event?.locationType === LocationType.ONLINE &&
						event?.type === EventTypeV2.SESSION &&
						event?.sessionPriceType === SessionPriceType.EACH
					) {
						ticketTiers = session?.tickets
							? _formatTicketTiers(session.tickets)
							: [{}];
					}

					if (event?.locationType === LocationType.OFFLINE) {
						if (event?.addressScope === AddressScope.EACH) {
							address = session?.address || "";
						}

						if (event?.ticketScope === TicketScope.EACH) {
							ticketTiers = session?.tickets
								? _formatTicketTiers(session.tickets)
								: [{}];
						}
					}

					return {
						roomProvider: session?.roomProvider
							? convertServerRoomProviderToClient(
									session.roomProvider,
									session.roomMadeBy || RoomMadeBy.SYSTEM,
								)
							: undefined,
						roomUrl: session?.roomUrl || undefined,
						roomInfo: session?.roomInfo || undefined,
						ticketTiers,
						address,
					};
				}) || [],
		},
		stepResource: {
			docs: event?.docs ? event.docs : undefined,
			whyChooses: event?.whyChooses ? event.whyChooses : [],
			objects: event?.objects ? event.objects : [],
			// @ts-ignore
			qas: event?.qas ? event.qas : [],
			// @ts-ignore
			agendas: event?.agendas ? event.agendas : [],
		},
		//
		productId: event?.product?.id || undefined,
		_id: event?.id || undefined,
	};
};

const _formatEventV2Input = async (payload: WizardSchemaType) => {
	const resources: {
		banner: CreateEventV2Input["banner"];
		photos: CreateEventV2Input["photos"];
		videos: CreateEventV2Input["videos"];
		agendas: CreateEventV2Input["agendas"];
		docs: CreateEventV2Input["docs"];
		whyChooses: CreateEventV2Input["whyChooses"];
		objects: CreateEventV2Input["objects"];
		qas: CreateEventV2Input["qas"];
		categories: CreateEventV2Input["categories"];
		productSurveys?: CreateEventV2Input["productSurveys"];
	} = {
		banner: "",
		photos: [],
		videos: [],
		agendas: [],
		docs: [],
		whyChooses: [],
		objects: [],
		qas: [],
		categories: [],
		productSurveys: [],
	};

	resources.banner =
		typeof payload.stepInfo.banner === "string"
			? payload.stepInfo.banner
			: (await uploadToS3([payload.stepInfo.banner as File]))?.[0];
	resources.categories = payload.stepInfo.categories.map(({ value }) => value);

	if ((payload?.stepInfo?.photos || [])?.length > 0) {
		resources.photos = await map(
			(payload.stepInfo.photos || []).filter(({ file }) => Boolean(file)),
			async (el: { file: string | File | null }) => {
				if (typeof el.file === "string") {
					return el.file;
				}

				return (await uploadToS3([el.file as File]))?.[0];
			},
		);
	}

	if ((payload?.stepInfo?.surveyIds || [])?.length > 0) {
		resources.productSurveys =
			(payload?.stepInfo?.surveyIds || []).length > 0
				? (payload?.stepInfo?.surveyIds || []).map((surveyId) => ({
						surveyId,
						required: payload?.stepInfo?.surveyRequired?.[surveyId] || false,
					}))
				: [];
	}

	if ((payload?.stepInfo?.videos || [])?.length > 0) {
		resources.videos =
			(payload?.stepInfo?.videos || []).length > 0
				? (payload?.stepInfo?.videos || [])
						.filter(({ url }) => Boolean(url))
						.map(({ url }) => url)
				: [];
	}

	if ((payload?.stepResource?.agendas || [])?.length > 0) {
		resources.agendas = await map(
			payload.stepResource?.agendas || [],
			// @ts-ignore
			async (el: WizardSchemaType["stepResource"]["agendas"][0]) => {
				if (typeof el.thumbnail === "string") {
					return el;
				}

				return {
					...el,
					thumbnail: el.thumbnail
						? (await uploadToS3([el.thumbnail as File]))?.[0]
						: null,
				};
			},
		);
	}

	if ((payload?.stepResource?.docs || [])?.length > 0) {
		resources.docs = await map(
			payload.stepResource?.docs || [],
			// @ts-ignore
			async (el: WizardSchemaType["stepResource"]["docs"][0]) => {
				if (typeof el === "string") {
					return el;
				}

				return (await uploadToS3([el as File]))?.[0];
			},
		);
	}

	if ((payload?.stepResource?.whyChooses || [])?.length > 0) {
		resources.whyChooses = payload.stepResource?.whyChooses?.filter(
			Boolean,
		) as string[];
	}

	if ((payload?.stepResource?.objects || [])?.length > 0) {
		resources.objects = payload.stepResource?.objects?.filter(
			Boolean,
		) as string[];
	}

	if ((payload?.stepResource?.qas || [])?.length > 0) {
		resources.qas = payload.stepResource?.qas || [];
	}

	return resources;
};

const _formatEventSessionInput = async (
	payload: WizardSchemaType,
	defaultValues: { banner?: string } = {},
): Promise<CreateEventV2Input["eventSessions"]> => {
	const { stepPricing, stepTimeline } = payload;
	const isPriceOnEachSession = stepPricing.priceType === SessionPriceType.EACH;
	const isOffline = payload.locationType === LocationType.OFFLINE;
	const ticketScope = stepPricing.ticketScope;
	const addressScope = stepPricing.addressScope;

	const eventSessions = (stepTimeline.sessions || []).map((session, index) => ({
		...session,
		order: index + 1,
	}));

	return await map(
		eventSessions,
		async (
			eventSession: WizardSchemaType["stepTimeline"]["sessions"][0] & {
				order: number;
			},
		) => {
			const index = eventSession.order - 1;
			let ticketTiers: any[] = [];

			if (isOffline) {
				if (ticketScope === TicketScope.EACH) {
					ticketTiers =
						stepPricing?.sessions?.[index]?.ticketTiers?.map((ticketTier) => ({
							id: ticketTier.id || undefined,
							name: ticketTier.name,
							description: ticketTier.description || null,
							price: ticketTier.farePrice || 0,
							limitAudiences: ticketTier.limitAudiences || null,
							limitTicketPerUser: ticketTier.limitTicketPerUser || null,
						})) || [];
				}

				if (addressScope === AddressScope.EACH) {
					eventSession.address = stepPricing?.sessions?.[index]?.address
						? stepPricing.sessions[index].address
						: "";
				}
			}

			if (!isOffline) {
				if (isPriceOnEachSession) {
					ticketTiers =
						stepPricing?.sessions?.[index]?.ticketTiers?.map((ticketTier) => ({
							id: ticketTier.id || undefined,
							name: ticketTier.name || "",
							description: ticketTier.description || null,
							price: ticketTier.farePrice || 0,
							limitAudiences: ticketTier.limitAudiences || null,
							limitTicketPerUser: ticketTier.limitTicketPerUser || null,
						})) || [];
				}
			}

			let { banner } = defaultValues;
			let coHostId = null;
			let coHostInfo = null;

			if (eventSession.coHostType === CoHostType.SYSTEM) {
				coHostId = eventSession.coHostEntity?.id || null;
			}

			if (
				eventSession.coHostType === CoHostType.EMAIL &&
				(eventSession.coHost?.name || eventSession.coHost?.email)
			) {
				coHostInfo = {
					name: eventSession.coHost?.name || null,
					email: eventSession.coHost.email || null,
				};
			}

			if (eventSession.banner) {
				banner =
					typeof eventSession.banner === "string"
						? eventSession.banner
						: (await uploadToS3([eventSession.banner as File]))?.[0];
			}

			return {
				id: eventSession._id || undefined,
				name: eventSession.name,
				content: eventSession.content,
				banner,
				order: eventSession.order,
				startDate: eventSession.startDate,
				endDate: eventSession.endDate,
				roomProvider: stepPricing?.sessions?.[index]?.roomProvider
					? convertClientRoomProviderToServer(
							stepPricing.sessions[index].roomProvider,
						)
					: null,
				roomUrl: stepPricing?.sessions?.[index]?.roomUrl || null,
				roomInfo: stepPricing?.sessions?.[index]?.roomInfo || null,
				roomMadeBy: stepPricing?.sessions?.[index]?.roomProvider
					? stepPricing.sessions[index].roomProvider ===
						ExtendRoomProvider.SYSTEM
						? RoomMadeBy.SYSTEM
						: RoomMadeBy.HOST
					: null,
				radioOptions: null,
				coHostId,
				coHostInfo,
				ticketTiers,
				address: eventSession.address,
			};
		},
	);
};

const _formatUpsertEventV2Input = async (payload: WizardSchemaType) => {
	const {
		banner,
		photos,
		videos,
		agendas,
		docs,
		whyChooses,
		objects,
		qas,
		categories,
		productSurveys,
	} = await _formatEventV2Input(payload);

	const { type, stepTimelineAndPricing, stepPricing } = payload;

	const input: Partial<CreateEventV2Input> = {
		type,
		locationType: payload.locationType,
		name: payload.stepInfo.name,
		content: addWhitespaceToEmptyTypo(payload?.stepInfo?.content || ""),
		hashtags: payload.stepInfo.hashtags,
		categories,
		photos,
		videos,
		banner,
		startDate: undefined,
		endDate: undefined,
		roomProvider: null,
		roomUrl: null,
		roomInfo: null,
		roomMadeBy: null,
		socialCommunities: [],
		sessionPriceType: null,
		ticketTiers: [],
		agendas,
		docs,
		whyChooses,
		objects,
		qas,
		eventSessions: [],
		address: null,
		productSurveys,
	};

	switch (payload.type) {
		case EventTypeV2.SINGLE: {
			input.startDate = stepTimelineAndPricing.startDate as string;
			input.endDate = stepTimelineAndPricing.endDate as string;
			input.roomProvider = stepTimelineAndPricing?.roomProvider
				? convertClientRoomProviderToServer(stepTimelineAndPricing.roomProvider)
				: null;
			input.roomUrl = stepTimelineAndPricing.roomUrl;
			input.roomInfo = stepTimelineAndPricing.roomInfo;
			input.roomMadeBy = stepTimelineAndPricing?.roomProvider
				? stepTimelineAndPricing.roomProvider === ExtendRoomProvider.SYSTEM
					? RoomMadeBy.SYSTEM
					: RoomMadeBy.HOST
				: null;
			input.socialCommunities = stepTimelineAndPricing.socialCommunities.map(
				(item) => ({
					socialCommunityType: item.socialCommunityType as SocialCommunityType,
					socialCommunityName: item.socialCommunityName || null,
					socialCommunityLink: item.socialCommunityLink || null,
				}),
			);
			input.ticketTiers = stepTimelineAndPricing.ticketTiers.map(
				(ticketTier) => ({
					id: ticketTier.id || undefined,
					name: ticketTier.name,
					description: ticketTier.description || null,
					price: ticketTier.farePrice || 0,
					limitAudiences: ticketTier.limitAudiences || null,
					limitTicketPerUser: ticketTier.limitTicketPerUser || null,
				}),
			);
			input.address = stepTimelineAndPricing.address || null;
			break;
		}
		case EventTypeV2.SESSION: {
			const isPriceOnAllSession =
				stepPricing.priceType === SessionPriceType.ALL;
			const isOffline = stepPricing.locationType === LocationType.OFFLINE;
			const addressScope = stepPricing.addressScope;
			const ticketScope = stepPricing.ticketScope;

			input.startDate = null;
			input.endDate = null;
			input.sessionPriceType = stepPricing.priceType;
			input.addressScope = addressScope;
			input.ticketScope = ticketScope;

			if (isOffline) {
				if (ticketScope === TicketScope.ALL) {
					input.ticketTiers = (stepPricing.ticketTiers || []).map(
						(ticketTier) => ({
							id: ticketTier.id || undefined,
							name: ticketTier.name,
							description: ticketTier.description || null,
							price: ticketTier.farePrice || 0,
							limitAudiences: ticketTier.limitAudiences || null,
							limitTicketPerUser: ticketTier.limitTicketPerUser || null,
						}),
					);
				}

				if (addressScope === AddressScope.ALL) {
					input.address = stepPricing.address || "";
				}
			}

			if (!isOffline) {
				if (isPriceOnAllSession) {
					input.ticketTiers = (stepPricing.ticketTiers || []).map(
						(ticketTier) => ({
							id: ticketTier.id || undefined,
							name: ticketTier.name,
							description: ticketTier.description || null,
							price: ticketTier.farePrice || 0,
							limitAudiences: ticketTier.limitAudiences || null,
							limitTicketPerUser: ticketTier.limitTicketPerUser || null,
						}),
					);
				}
			}

			input.socialCommunities = stepPricing.socialCommunities.map((item) => ({
				socialCommunityType: item.socialCommunityType as SocialCommunityType,
				socialCommunityName: item.socialCommunityName || null,
				socialCommunityLink: item.socialCommunityLink || null,
			}));

			input.eventSessions = await _formatEventSessionInput(payload, {
				banner,
			});

			break;
		}
		default: {
			// @ts-ignore
			throw new Error(`Invalid event type: ${payload.type}`);
		}
	}

	return input;
};

export const createEventV2 = async (
	payload: WizardSchemaType,
	{
		isDraft = false,
		isPreview = false,
	}: { isDraft?: boolean; isPreview?: boolean } = {},
): Promise<EventV2Response> => {
	const input = await _formatUpsertEventV2Input(payload);

	if (isDraft) {
		input.isPreview = isPreview;
		const res = await restRepository.api.draftEventV2(
			input as DraftEventV2Input,
		);
		return res.data;
	}

	const res = await restRepository.api.createEventV2(
		input as CreateEventV2Input,
	);

	return res.data;
};

export const updateEventV2 = async (
	id: string,
	payload: WizardSchemaType,
	{ isDraft = false }: { isDraft?: boolean },
): Promise<EventV2Response> => {
	const input = await _formatUpsertEventV2Input(payload);
	input.isPreview = false;

	if (isDraft) {
		const res = await restRepository.api.updateDraftEventV2(
			id,
			input as DraftEventV2Input,
		);
		return res.data;
	}

	const res = await restRepository.api.updateEventV2(
		id,
		input as CreateEventV2Input,
	);

	return res.data;
};
